import type { CallSummary } from '@/lib/calls';
import type { SubscriptionInfo } from '@/lib/subscription';

export interface EstablishmentRow {
  id: string;
  name: string;
  city: string | null;
  /** Регистрация заведения. */
  createdAt: string;
  attachedAt: string | null;
  commissionBps: number | null;
  source: string | null;
  /** Точка привязана к тому, кто смотрит (главный видит и чужие). */
  mine: boolean;
  /** Чья это точка — заполнено у главного менеджера. */
  referrer: { id: string; name: string; code: string } | null;
  /** Телефоны для звонка прямо из списка. */
  phone: string | null;
  ownerName: string | null;
  ownerPhone: string | null;
  subscription: SubscriptionInfo | null;
  /** Сколько точек у владельца; у каждой своя подписка и оплата. */
  networkSize: number;
  /** Оборот заведения: выручка через кассу за 30 дней. */
  revenue30dKopecks: number;
  /** Оплаты подписки (не касса). */
  paymentsTotalKopecks: number;
  paymentsCount: number;
  commissionTotalKopecks: number;
  lastPaymentAt: string | null;
  /** Прозвон: последний звонок; null — ещё не звонили. Нет у старого сервера. */
  call?: CallSummary | null;
}

export const SORTS = [
  { value: 'attached_desc', label: 'Сначала новые' },
  { value: 'attached_asc', label: 'Сначала старые' },
  { value: 'days_asc', label: 'Срок: скоро истечёт' },
  { value: 'days_desc', label: 'Срок: больше всего дней' },
  { value: 'revenue_desc', label: 'Оборот 30 дн.: больше' },
  { value: 'revenue_asc', label: 'Оборот 30 дн.: меньше' },
  { value: 'payment_desc', label: 'Последняя оплата' },
  { value: 'commission_desc', label: 'Комиссия: больше' },
  { value: 'commission_asc', label: 'Комиссия: меньше' },
  { value: 'name_asc', label: 'По названию' },
] as const;

export type SortValue = (typeof SORTS)[number]['value'];

export function parseSort(raw: string | null): SortValue {
  return SORTS.some((s) => s.value === raw) ? (raw as SortValue) : 'attached_desc';
}

/** Горит: доступ уже просрочен или кончается в ближайшие 3 дня. */
export function isExpiring(row: EstablishmentRow): boolean {
  const sub = row.subscription;
  return !!sub && (sub.accessState !== 'ok' || (sub.daysLeft ?? 99) <= 3);
}

// Список держим в памяти между заходами: при возврате из карточки он рисуется
// сразу на полную высоту, иначе восстановить прокрутку было бы не к чему.
// Ключ — менеджер, его роль (главный видит все точки) и сортировка (сортирует сервер).
const cache = new Map<string, EstablishmentRow[]>();

export function establishmentsCacheScope(managerId: string, role: string | undefined): string {
  return `${managerId}:${role ?? 'MANAGER'}`;
}

const cacheKey = (scope: string, sort: SortValue) => `${scope}:${sort}`;

export function readEstablishmentsCache(scope: string, sort: SortValue): EstablishmentRow[] | null {
  return cache.get(cacheKey(scope, sort)) ?? null;
}

export function writeEstablishmentsCache(scope: string, sort: SortValue, rows: EstablishmentRow[]) {
  cache.set(cacheKey(scope, sort), rows);
}

/** После действия в карточке: строка в списке сразу показывает новый срок. */
export function patchCachedSubscription(establishmentId: string, subscription: SubscriptionInfo | null) {
  for (const [key, rows] of cache) {
    if (!rows.some((r) => r.id === establishmentId)) continue;
    cache.set(
      key,
      rows.map((r) => (r.id === establishmentId ? { ...r, subscription } : r)),
    );
  }
}

/** После отметки звонка: строка в списке сразу переходит в свою группу прозвона. */
export function patchCachedCall(establishmentId: string, call: CallSummary | null) {
  for (const [key, rows] of cache) {
    if (!rows.some((r) => r.id === establishmentId)) continue;
    cache.set(
      key,
      rows.map((r) => (r.id === establishmentId ? { ...r, call } : r)),
    );
  }
}

export function clearEstablishmentsCache() {
  cache.clear();
}
